import React from 'react';
import { Heart, MessageSquare, Clock, Send } from 'lucide-react';

const FeedbackTab = ({ pulseSurvey, surveys, activeSurvey, setActiveSurvey, surveyResponse, setSurveyResponse, isLoading, respondToPulse, submitSurveyResponse }) => (
  <div className="space-y-6">
    {/* Pulse Survey */}
    {pulseSurvey && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Heart className="w-5 h-5 mr-2 text-red-500" />
          Quick Pulse
        </h3>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{pulseSurvey.question}</p>

        <div className="flex gap-4 justify-center">
          {pulseSurvey.emoji_options.map((emoji, index) => (
            <button
              key={index}
              onClick={() => respondToPulse(emoji)}
              className="text-4xl hover:scale-110 transition-transform duration-200"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    )}

    {/* Active Surveys */}
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
        Available Surveys
      </h3>

      {surveys.filter(s => s.status === 'active').length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No active surveys available</p>
          <p className="text-sm">Check back later for new feedback opportunities</p>
        </div>
      ) : (
        <div className="space-y-4">
          {surveys.filter(s => s.status === 'active').map((survey) => (
            <div key={survey.id} className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {survey.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {survey.description}
                  </p>
                  <div className="flex items-center mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>{survey.questions?.length || 0} questions</span>
                    <span className="mx-2">•</span>
                    <span>~{(survey.questions?.length || 0) * 2} min</span>
                  </div>
                </div>

                <button
                  onClick={() => setActiveSurvey(survey)}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Take Survey
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Survey Modal */}
    {activeSurvey && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {activeSurvey.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  {activeSurvey.description}
                </p>
              </div>
              <button
                onClick={() => setActiveSurvey(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {activeSurvey.questions?.map((question, index) => (
                <div key={question.id} className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {index + 1}. {question.question_text}
                    {question.is_required && <span className="text-red-500 ml-1">*</span>}
                  </label>

                  {question.question_type === 'multiple_choice' && (
                    <div className="space-y-2">
                      {question.options.map((option, optIndex) => (
                        <label key={optIndex} className="flex items-center">
                          <input
                            type="radio"
                            name={`question_${question.id}`}
                            value={option}
                            onChange={(e) => setSurveyResponse(prev => ({
                              ...prev,
                              [question.id]: e.target.value
                            }))}
                            className="mr-2"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.question_type === 'rating_scale' && (
                    <div className="flex gap-2">
                      {Array.from({ length: question.max_rating - question.min_rating + 1 }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setSurveyResponse(prev => ({
                            ...prev,
                            [question.id]: question.min_rating + i
                          }))}
                          className={`w-8 h-8 rounded-full border ${
                            surveyResponse[question.id] === question.min_rating + i
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {question.min_rating + i}
                        </button>
                      ))}
                    </div>
                  )}

                  {question.question_type === 'text' && (
                    <textarea
                      value={surveyResponse[question.id] || ''}
                      onChange={(e) => setSurveyResponse(prev => ({
                        ...prev,
                        [question.id]: e.target.value
                      }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Your response..."
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setActiveSurvey(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => submitSurveyResponse(activeSurvey.id)}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                {isLoading ? 'Submitting...' : 'Submit Survey'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

export default FeedbackTab;
